import { css } from "@emotion/react"
import cx from "classnames"
import { useContext } from "react"
import { Link } from "react-router-dom"
import { isAddressComplete } from "source/giving/utils/donor"
import { WebBootContext } from "source/publishing/WebBoot"
import { Icon } from "source/shared/components"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { useSession } from "source/shared/hooks/useSession"
import { PersonAddress } from "source/shared/types"
import { PostDonateSuccessLinks } from "./PostDonateSuccessLinks"

type PostDonateStatusType =
  | "success"
  | "processing"
  | "action-required"
  | "failure"

interface PostDonateStatusProps {
  heading: string
  message: string
  status: PostDonateStatusType
}

function getIconProps(status: PostDonateStatusType) {
  switch (status) {
    case "success":
    case "processing":
      return {
        symbol: "general#check-circle",
        className: "c-emerald",
      }
    case "action-required":
      return {
        symbol: "general#exclamation-triangle",
        className: "c-marigold",
      }
    case "failure":
      return {
        symbol: "general#exclamation-triangle",
        className: "c-ruby",
      }
  }
}

export function PostDonateStatus({
  heading,
  message,
  status,
}: PostDonateStatusProps) {
  const { currentOrganization } = useContext(WebBootContext)
  const {
    data: {
      attributes: { address },
    },
  } = useSession()
  const {
    current_organization: { contact_website },
  } = useContext(ServerRenderedProps)

  const { symbol, className } = getIconProps(status)
  const solicitAddress =
    !isAddressComplete(address as PersonAddress) &&
    (status === "success" || status === "processing")

  return (
    <section className="d-f fd-c py-2 g-3">
      <div className="ta-c">
        <Icon
          symbol={symbol}
          className={cx("mb-3", className)}
          aria-hidden
          css={css`
            width: 3rem;
            height: 3rem;
          `}
        />
        <div
          className="d-f fd-c g-2 px-4@sm"
          css={css`
            text-wrap: pretty;
          `}
        >
          <h1>{heading}</h1>
          <p className="m-0">{message}</p>
          {solicitAddress && (
            <p className="m-0">
              {currentOrganization?.attributes?.country_code === "CA"
                ? "To ensure your receipt is valid for tax purposes, the Canada Revenue Agency requires your mailing address."
                : "Please add a mailing address to complete your profile."}
            </p>
          )}
        </div>
      </div>
      <footer className="d-f fd-c mx-a g-1">
        {status === "failure" ? (
          <Link to="/giving" className="btn primary-btn">
            Try again
          </Link>
        ) : (
          <PostDonateSuccessLinks
            solicitAddress={solicitAddress}
            orgWebsite={contact_website}
          />
        )}
      </footer>
    </section>
  )
}
